import React from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import type { IntegrationLinkDto } from "@/api/integrations/links";
import type { ProviderState, SourceRow } from "./useIntegrationsData";

const GRID = "minmax(190px, 2fr) minmax(150px, 1.3fr) minmax(105px, 1fr) minmax(105px, 1fr) minmax(105px, 1fr) 110px";

interface TableProps {
    state: ProviderState;
    productNameById: Map<number, string>;
    syncingIds: ReadonlySet<number>;
    pendingUnlinkId: number | null;
    onSync: (row: SourceRow, link: IntegrationLinkDto) => void;
    onUnlink: (row: SourceRow, link: IntegrationLinkDto) => void;
}

interface ProductLinkRow {
    source: SourceRow;
    link: IntegrationLinkDto;
}

export function connectedProductRows(rows: SourceRow[]): ProductLinkRow[] {
    return rows.flatMap(source => source.links.map(link => ({ source, link })));
}

export function formatRelativeTime(timestamp: string | null, now = Date.now()): string {
    if (!timestamp) return "Never synced";
    const parsed = Date.parse(timestamp);
    if (!Number.isFinite(parsed)) return "—";
    const elapsedSeconds = Math.max(0, Math.floor((now - parsed) / 1000));
    if (elapsedSeconds < 60) return "Just now";

    const intervals: Array<[number, string]> = [
        [31_536_000, "year"],
        [2_592_000, "month"],
        [604_800, "week"],
        [86_400, "day"],
        [3_600, "hour"],
        [60, "minute"]
    ];
    for (const [seconds, unit] of intervals) {
        if (elapsedSeconds >= seconds) {
            const value = Math.floor(elapsedSeconds / seconds);
            return `${value} ${unit}${value === 1 ? "" : "s"} ago`;
        }
    }
    return "Just now";
}

function projectLabel(state: ProviderState, row: SourceRow, link: IntegrationLinkDto): string {
    if (state.meta.id === "azure-devops") {
        const organization = state.organization || state.siteOrOrg;
        return organization ? `${row.externalName} · ${organization}` : row.externalName;
    }
    const linkedBranch = typeof link.metadata?.branch === "string"
        ? link.metadata.branch.trim()
        : "";
    const identifier = state.meta.kind === "git"
        ? (linkedBranch || row.keyOrBranch)
        : row.keyOrBranch;
    return identifier ? `${row.externalName} · ${identifier}` : row.externalName;
}

function syncData(link: IntegrationLinkDto): string {
    if (link.syncedCount == null || !link.syncedUnit) return "Not synced";
    return `${link.syncedCount.toLocaleString()} ${link.syncedUnit}`;
}

function statusDetails(link: IntegrationLinkDto, syncing: boolean) {
    if (syncing) return { label: "Syncing…", color: "var(--primary)" };
    switch (link.syncStatus) {
        case "synced": return { label: "Synced", color: "var(--success)" };
        case "failed": return { label: "Sync failed", color: "var(--error)" };
        default: return { label: "Needs review", color: "#d97706" };
    }
}

function LinkRow({ state, source, link, productNameById, syncingIds, pendingUnlinkId, onSync, onUnlink }: TableProps & ProductLinkRow) {
    const productName = productNameById.get(link.projectId) || `Product ${link.projectId}`;
    const syncing = syncingIds.has(link.id);
    const pendingRemoval = pendingUnlinkId === link.id;
    const status = statusDetails(link, syncing);
    const sourceLabel = projectLabel(state, source, link);

    return (
        <div className="il-product-row" style={{ gridTemplateColumns: GRID }}>
            <div className="il-product-cell" title={productName}>
                <span className="il-product-initial">{(productName.trim()[0] || "?").toUpperCase()}</span>
                <span className="il-product-name">{productName}</span>
            </div>
            <div className="il-source-label" title={sourceLabel}>{sourceLabel}</div>
            <div className="il-status" role="status" aria-live="polite" title={link.lastSyncError || undefined}>
                <span className="il-status-dot" style={{ background: status.color }} />
                <span style={{ color: status.color }}>{status.label}</span>
            </div>
            <span className="il-cell-text">{formatRelativeTime(link.lastSyncedAt)}</span>
            <span className="il-cell-text">{syncData(link)}</span>
            <div className="il-row-actions">
                <button
                    type="button"
                    className="il-icon-button"
                    aria-label={`Sync ${productName} now`}
                    style={{ width: 44, height: 44 }}
                    disabled={syncing || pendingRemoval}
                    onClick={() => onSync(source, link)}
                >
                    <RefreshCw className={syncing ? "il-spin" : undefined} size={16} strokeWidth={1.9} />
                </button>
                <button
                    type="button"
                    className="il-icon-button il-icon-button-danger"
                    aria-label={`Remove ${productName} connection`}
                    style={{ width: 44, height: 44 }}
                    disabled={syncing || pendingRemoval}
                    onClick={() => onUnlink(source, link)}
                >
                    <Trash2 size={16} strokeWidth={1.9} />
                </button>
            </div>
        </div>
    );
}

export function LinkTable(props: TableProps) {
    const productRows = connectedProductRows(props.state.rows);
    if (productRows.length === 0) {
        return <div className="il-empty-state">No products connected yet.</div>;
    }

    return (
        <div className="il-link-table">
            <div className="il-table-header" style={{ gridTemplateColumns: GRID }}>
                <span className="il-colhead">Product</span>
                <span className="il-colhead">{props.state.meta.connectedColumnLabel}</span>
                <span className="il-colhead">Status</span>
                <span className="il-colhead">Last Synced</span>
                <span className="il-colhead">Data Synced</span>
                <span className="il-colhead" style={{ textAlign: "right" }}>Actions</span>
            </div>
            {productRows.map(({ source, link }) => (
                <LinkRow key={link.id} {...props} source={source} link={link} />
            ))}
        </div>
    );
}
